import React, { FC } from 'react';
import { Button, Stack } from '@mui/material';
import { ISubscriptionSearchTerms } from '../../types/subscription-role';
import { onKeyDown } from '../../util/onKeyDown';
import { KeyboardKey } from '../../types/keyboardKey';
import { FormikTextField } from '../General/FormikTextField';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import LoadingButton from '@mui/lab/LoadingButton';

const SubscriptionSearchBar: FC<{
  subscriptionSearchParams: ISubscriptionSearchTerms | null;
  setSubscriptionSearchParams: React.Dispatch<
    React.SetStateAction<ISubscriptionSearchTerms | null>
  >;
  loading: boolean;
}> = ({ subscriptionSearchParams, setSubscriptionSearchParams, loading }) => {
  const ValidationSchema = Yup.object({
    subscriptionId: Yup.string().required('Subscription ID is required'),
  });

  type FormValues = Yup.InferType<typeof ValidationSchema>;

  const initialValues: FormValues = {
    subscriptionId: subscriptionSearchParams?.subscriptionId || '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formik) => {
        if (values.subscriptionId && values.subscriptionId.trim() !== '') {
          setSubscriptionSearchParams({ subscriptionId: values.subscriptionId });
        }
        formik.setStatus({ justSubmitted: true });
      }}
      onReset={(values, formik) => {
        formik.setStatus({ justSubmitted: false });
        setSubscriptionSearchParams(null);
      }}
      validationSchema={ValidationSchema}>
      {(formik) => (
        <Form noValidate>
          <Stack
            alignItems="flex-end"
            direction="row"
            my={3}
            spacing={1}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              formik.setStatus({ justSubmitted: false });

              if (!formik.isValid || !formik.dirty)
                onKeyDown(e, KeyboardKey.ENTER, formik.submitForm);
            }}>
            <FormikTextField
              name="subscriptionId"
              formikId="subscriptionSearch"
              label="Subscription ID"
              placeholder="Subscription ID"
              optionalLabel="Only exact matches will be returned. Wildcards (*) are not allowed"
              errors={formik.errors}
              touched={formik.touched}
              setFieldValue={formik.setFieldValue}
              preventWhitespace
            />
            <LoadingButton
              id="findSubscription"
              disabled={!formik.isValid || formik.status?.justSubmitted === true}
              loading={loading}
              type="submit"
              variant="brandContained"
              color="brandBlack"
              sx={{ height: '56px' }}
              data-cy={'btn_find_subscription'}>
              Find user
            </LoadingButton>
            <Button
              id="resetSubscription"
              type="reset"
              variant="brandContained"
              color="brandBlack"
              sx={{ height: '56px' }}
              data-testid="btn_reset_subscription"
              data-cy={'btn_reset_subscription'}>
              Reset
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default SubscriptionSearchBar;
